import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { 
  MapPin, 
  Calendar, 
  Users, 
  Award, 
  Home, 
  Hammer, 
  TrendingUp,
  Star,
  Heart,
  Building
} from "lucide-react";

const About = () => {
  const milestones = [
    {
      year: "2014",
      title: "First Property Purchase",
      description: "Aaron bought his first condo, beginning his real estate journey",
      icon: Home
    },
    {
      year: "2015-2018", 
      title: "Learning & Growing",
      description: "Developed renovation skills and built relationships with contractors",
      icon: Hammer
    },
    {
      year: "2019-2022",
      title: "Expansion Phase", 
      description: "Scaled operations and completed multiple high-end renovations",
      icon: TrendingUp
    },
    {
      year: "2023-2025",
      title: "Market Leadership",
      description: "Established Sddolphinflipper as a premier San Diego developer",
      icon: Award
    }
  ];

  const values = [
    {
      title: "Creative Design",
      description: "Every property features unique style, innovative textures, and modern aesthetics",
      icon: Star,
      color: "amber"
    },
    {
      title: "Community Focus",
      description: "Supporting local San Diego craftsmen and creating employment opportunities", 
      icon: Users,
      color: "orange"
    },
    {
      title: "Quality Excellence",
      description: "Uncompromising standards in materials, workmanship, and final results",
      icon: Award,
      color: "red"
    },
    {
      title: "Sustainable Growth",
      description: "Building lasting value while respecting neighborhood character",
      icon: Building,
      color: "green"
    }
  ];

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
      {/* Header Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              About <span className="text-amber-600">Aaron Hands</span>
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the visionary behind Sddolphinflipper - a passionate real estate developer 
              who has transformed the San Diego property landscape one renovation at a time.
            </p>
          </div>

          {/* Hero Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
            <Card className="bg-white/80 backdrop-blur-sm border-amber-200 text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-bold text-amber-600 mb-2">2014</div>
                <div className="text-gray-600">Started Journey</div>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur-sm border-orange-200 text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-bold text-orange-600 mb-2">7</div>
                <div className="text-gray-600">Properties Completed</div>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur-sm border-red-200 text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-bold text-red-600 mb-2">50+</div>
                <div className="text-gray-600">Workers Employed</div>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur-sm border-green-200 text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-bold text-green-600 mb-2">11</div>
                <div className="text-gray-600">Years Experience</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white/90">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                The <span className="text-amber-600">Sddolphinflipper</span> Story
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto"></div>
            </div>

            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 shadow-xl mb-12">
              <CardContent className="p-8">
                <div className="flex items-start space-x-6">
                  <div className="bg-amber-500 p-4 rounded-full flex-shrink-0">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">A Passion Born from Vision</h3>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      In 2014, Aaron Hands made a decision that would change his life forever - he bought his first condo. 
                      What began as a personal investment quickly evolved into something much greater: a passion for transforming 
                      properties and creating beautiful living spaces throughout San Diego County.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-start space-x-6">
                  <div className="bg-orange-500 p-4 rounded-full flex-shrink-0">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Building Community Through Development</h3>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      Aaron's journey isn't just about properties - it's about people. Through his developments, 
                      he has employed over 50 San Diego men and women, from skilled craftsmen to creative designers. 
                      Each project becomes a collaboration that strengthens the local community while creating 
                      exceptional living spaces.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Journey <span className="text-amber-600">Timeline</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto mb-8"></div>
          </div>

          <div className="max-w-4xl mx-auto">
            {milestones.map((milestone, index) => {
              const IconComponent = milestone.icon;
              return (
                <div key={index} className="flex items-center mb-12 last:mb-0">
                  <div className="flex-shrink-0 w-32 text-right mr-8 hidden md:block">
                    <Badge className="bg-amber-500 text-white text-lg px-4 py-2">
                      {milestone.year}
                    </Badge>
                  </div>
                  
                  <div className="flex-shrink-0 w-4 h-4 bg-amber-500 rounded-full border-4 border-white shadow-lg relative z-10"></div>
                  
                  <Card className="flex-1 ml-8 bg-white/90 backdrop-blur-sm border-amber-200 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-amber-500 p-3 rounded-full">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="md:hidden mb-2">
                            <Badge className="bg-amber-500 text-white">
                              {milestone.year}
                            </Badge>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.title}</h3>
                          <p className="text-gray-700">{milestone.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-amber-600">Core Values</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every project reflects our commitment to excellence, creativity, and community impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              const colorClass = {
                amber: "from-amber-50 to-orange-50 border-amber-200",
                orange: "from-orange-50 to-red-50 border-orange-200", 
                red: "from-red-50 to-amber-50 border-red-200",
                green: "from-green-50 to-emerald-50 border-green-200"
              };
              const iconColorClass = {
                amber: "bg-amber-500",
                orange: "bg-orange-500",
                red: "bg-red-500", 
                green: "bg-green-500"
              };
              
              return (
                <Card key={index} className={`bg-gradient-to-br ${colorClass[value.color]} shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2`}>
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-6">
                      <div className={`${iconColorClass[value.color]} p-4 rounded-full flex-shrink-0`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                        <p className="text-gray-700 leading-relaxed">{value.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-gray-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Work <span className="text-amber-400">Together?</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join Aaron Hands and the Sddolphinflipper team in creating the next chapter 
            of exceptional San Diego real estate development.
          </p>
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-10 py-4 text-lg font-medium rounded-full shadow-2xl hover:scale-105 transition-all duration-300"
          >
            <MapPin className="w-5 h-5 mr-2" />
            Start Your Project
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
